import React, { useState, useEffect, useRef, Component, ErrorInfo } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarIcon, Download, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormDescription } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import BottomNavigation from "@/components/BottomNavigation";
import { ScreenHeader } from "@/components/redesign";
import { ReportType, ReportProject, Report } from "@/types";
import { toast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import ReportModal from "@/components/reports/ReportModal";
import { reportsAPI } from "@/api/reports";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import FileExport from '../plugins/fileExport';
import { savePdfToDevice } from '../utils/pdfExport';
import { formatInDeviceTimezone } from "@/utils/dateUtils";
import useSubscription from "@/contexts/SubscriptionContext";
import { canGenerateDetailedReport } from "@/lib/featureGates";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

// Mock data for projects
const mockProjects: ReportProject[] = [
  { id: "1", name: "Sahil Villa Projesi" },
  { id: "2", name: "Merkez Ofis Renovasyonu" },
  { id: "3", name: "Yeni Apartman Binası" }
];

interface ReportFormValues {
  projectId: string;
  type: ReportType;
  startDate: Date | undefined;
  endDate: Date | undefined;
  includePhotos: boolean;
  includeAllRecords: boolean;
}

// Error boundary for catching render errors
class ErrorBoundary extends Component<
  { children: React.ReactNode, fallback?: React.ReactNode },
  { hasError: boolean, error: Error | null }
> {
  constructor(props: { children: React.ReactNode, fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    
    
    
    // Log additional error details for debugging
    if (error.message) {
      
    }
    if (error.stack) {
      
    }
    
    // Log error as JSON for better debugging in mobile environments
    try {
      const errorDetails = {
        message: error.message,
        name: error.name,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      };
      
    } catch (jsonError) {
      
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="p-4 border border-error-600/20 rounded-2xl bg-error-50 text-error-600">
          <h3 className="font-medium mb-2">Bir hata oluştu</h3>
          <p className="text-sm mb-2">
            İçerik yüklenirken bir sorun oluştu.
          </p>
          {this.state.error && (
            <p className="text-xs font-mono overflow-auto max-h-20 p-2 bg-white/50 rounded">
              {this.state.error.message}
            </p>
          )}
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-2 px-3 py-1 text-xs rounded-md bg-brand-600 text-white"
          >
            Yeniden Dene
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { status: subscriptionStatus } = useSubscription();
  const detailedReportAllowed = canGenerateDetailedReport(subscriptionStatus);
  const defaultReportType: ReportType = detailedReportAllowed ? "detailed" : "general";
  const [selectedReportType, setSelectedReportType] = useState<ReportType>(defaultReportType);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState<Report | null>(null);
  const [projects, setProjects] = useState<ReportProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reportHtml, setReportHtml] = useState<string | null>(null);

  const form = useForm<ReportFormValues>({
    defaultValues: {
      projectId: "",
      type: defaultReportType,
      includePhotos: true,
      includeAllRecords: false
    }
  });

  useEffect(() => {
    // Load available projects when the component mounts
    const loadProjects = async () => {
      try {
        const { data, error } = await reportsAPI.getAvailableProjects();
        if (error) throw error;
        if (data) {
          setProjects(data);
        }
      } catch (error) {
        
        toast({
          title: t('reports.error_loading_projects'),
          description: t('reports.could_not_load_projects'),
          variant: "destructive"
        });
      }
    };

    loadProjects();
  }, [t]);

  const onSubmit = async (data: ReportFormValues) => {
    // Validation
    if (!data.projectId) {
      toast({
        title: t('reports.project_selection_required'),
        description: t('reports.project_selection_required_description'),
        variant: "destructive"
      });
      return;
    }

    // Only require dates if "Tumu" (includeAllRecords) is not selected
    if (!data.includeAllRecords && (!data.startDate || !data.endDate)) {
      toast({
        title: t('reports.date_range_required'),
        description: t('reports.date_range_required_description'),
        variant: "destructive"
      });
      return;
    }

    // Create a report object
    const newReport: Report = {
      id: Math.random().toString(36).substr(2, 9),
      type: data.type,
      projectId: data.projectId,
      startDate: data.includeAllRecords ? "" : (data.startDate ? data.startDate.toISOString() : ""),
      endDate: data.includeAllRecords ? "" : (data.endDate ? data.endDate.toISOString() : ""),
      includePhotos: data.includePhotos,
      includeSignatures: false
    };

    // Set current report and open modal
    setCurrentReport(newReport);
    setIsModalOpen(true);
  };

  const generateReport = (type: ReportType) => {
    if (type === "detailed" && !detailedReportAllowed) {
      toast({
        title: t('reports.premium_required_title', { defaultValue: 'Premium gerekli' }),
        description: t('reports.detailed_premium_required', {
          defaultValue: 'Detaylı rapor oluşturmak için Premium aboneliğe yükseltin.',
        }),
        variant: "destructive",
      });
      navigate('/subscription');
      return;
    }
    setSelectedReportType(type);
    form.setValue("type", type);

    toast({
      title: t('reports.preparing_report_settings'),
      description: type === "detailed"
        ? t('reports.configuring_detailed_report')
        : t('reports.configuring_general_report'),
    });
  };

  const handleExportPDF = async () => {
    setIsLoading(true);
    

    try {
      const formData = form.getValues();
      
      
      // Validate form data - only require dates if not including all records
      if (!formData.projectId || (!formData.includeAllRecords && (!formData.startDate || !formData.endDate))) {
        
        toast({
          title: t('reports.missing_information'),
          description: formData.includeAllRecords 
            ? t('reports.missing_info_project_only')
            : t('reports.missing_info_dates_or_all'),
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }

      toast({
        title: t('reports.preparing_report'),
        description: t('reports.report_preparing'),
      });

      

      // Call the API to generate the report HTML
      const { data, error } = await reportsAPI.generateReportHTML({
        projectId: formData.projectId,
        type: formData.type,
        startDate: formData.includeAllRecords ? undefined : formData.startDate?.toISOString(),
        endDate: formData.includeAllRecords ? undefined : formData.endDate?.toISOString(),
        includePhotos: formData.includePhotos,
        includeAllRecords: formData.includeAllRecords // Pass the new option to API
      });

      if (error) {
        
        throw error;
      }
      
      

      if (data?.htmlContent) {
        
        
        // Store the HTML content in state
        setReportHtml(data.htmlContent);
        
        // In mobile environments, always use the inline view for better reliability
        if (window.innerWidth < 768) { // Check if we're on a mobile device
          
          toast({
            title: t('reports.report_ready'),
            description: t('reports.report_ready_description'),
          });
          return; // Exit early and let the reportHtml state render the inline view
        }
        
        try {
          // Try to open the report in a popup window (desktop only)
          const reportWindow = window.open('', '_blank');
          if (reportWindow) {
            reportWindow.document.open();
            
            // Simplified popup content with direct PDF export
            reportWindow.document.write(`
              <!DOCTYPE html>
              <html>
              <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Rapor Önizleme</title>
                <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js" defer></script>
                <script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js" defer></script>
                <style>
                  body {
                    margin: 0;
                    font-family: Arial, sans-serif;
                  }
                  .header {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    background: white;
                    padding: 10px;
                    text-align: center;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    z-index: 1000;
                    display: flex;
                    justify-content: center;
                    gap: 10px;
                  }
                  .content {
                    margin-top: 60px;
                    padding: 20px;
                  }
                  button {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 4px;
                    font-size: 14px;
                    cursor: pointer;
                  }
                  .pdf-btn {
                    background-color: #4F46E5;
                    color: white;
                  }
                  .print-btn {
                    background-color: #10B981;
                    color: white;
                  }
                  .close-btn {
                    background-color: #EF4444;
                    color: white;
                  }
                  @media print {
                    .header { display: none; }
                    .content { margin-top: 0; }
                  }
                </style>
              </head>
              <body>
                <div class="header">
                  <button class="pdf-btn" id="pdf-btn">PDF İndir</button>
                  <button class="print-btn" id="print-btn">Yazdır</button>
                  <button class="close-btn" id="close-btn">Kapat</button>
                </div>
                <div class="content" id="report-content">
                  ${data.htmlContent}
                </div>
                
                <script>
                  // Wait for libraries to load
                  window.addEventListener('load', function() {
                    document.getElementById('close-btn').addEventListener('click', () => {
                      window.close();
                    });
                    
                    document.getElementById('print-btn').addEventListener('click', () => {
                      window.print();
                    });
                    
                    // Only set up PDF export if libraries loaded successfully
                    if (window.jspdf && window.html2canvas) {
                      document.getElementById('pdf-btn').addEventListener('click', async () => {
                        try {
                          const content = document.getElementById('report-content');
                          const name = document.querySelector('h1')?.textContent?.split('-')[0]?.trim() || 'Rapor';
                          
                          // Create PDF
                          const canvas = await html2canvas(content, {
                            scale: 1,
                            useCORS: true,
                            backgroundColor: 'white'
                          });
                          
                          const { jsPDF } = window.jspdf;
                          const pdf = new jsPDF('p', 'mm', 'a4');
                          
                          const imgWidth = 210;
                          const imgHeight = (canvas.height * imgWidth) / canvas.width;
                          
                          pdf.addImage(canvas.toDataURL('image/jpeg'), 'JPEG', 0, 0, imgWidth, imgHeight);
                          
                          // Handle multi-page
                          if (imgHeight > 295) {
                            let position = -295;
                            for (let i = 1; i < Math.ceil(imgHeight/295); i++) {
                              pdf.addPage();
                              pdf.addImage(canvas.toDataURL('image/jpeg'), 'JPEG', 0, position, imgWidth, imgHeight);
                              position -= 295;
                            }
                          }
                          
                          // Generate filename
                          const filename = name + '-' + new Date().toLocaleDateString('tr-TR') + '.pdf';
                          
                          // Check if we're on a native app using Capacitor
                          if (window.Capacitor?.isNativePlatform()) {
                            try {
                              
                              
                              // Use window.webkit.messageHandlers to communicate with native app
                              window.webkit.messageHandlers.fileExport.postMessage({
                                action: 'saveFile',
                                filename: filename,
                                data: pdf.output('datauristring').split(',')[1],
                                mimeType: 'application/pdf'
                              });
                              
                              // The native handler will show a success message
                            } catch (err) {
                              
                              // Fallback to regular download
                              pdf.save(filename);
                            }
                          } else {
                            // Regular save for browsers
                            pdf.save(filename);
                          }
                        } catch(e) {
                          
                          alert("PDF oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.");
                        }
                      });
                    } else {
                      const pdfBtn = document.getElementById('pdf-btn');
                      pdfBtn.textContent = "PDF oluşturulamıyor";
                      pdfBtn.disabled = true;
                      pdfBtn.style.backgroundColor = "#9CA3AF";
                    }
                  });
                </script>
              </body>
              </html>
            `);
            
            reportWindow.document.close();
            
            toast({
              title: t('reports.report_window_opened'),
              description: t('reports.report_window_opened_description'),
            });
          } else {
            throw new Error("Popup blocked");
          }
        } catch (error) {
          // If popup is blocked or fails, we'll show it inline
          
          toast({
            title: t('reports.report_inline_view'),
            description: t('reports.report_inline_view_description'),
          });
          // The reportHtml state is already set, so the inline view will be shown
        }
      } else {
        
        toast({
          title: t('reports.report_creation_error'),
          description: t('reports.report_content_error'),
          variant: "destructive"
        });
      }
    } catch (error) {
      
      toast({
        title: t('reports.report_creation_error'),
        description: t('reports.report_error_general'),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      
    }
  };

  // If we have HTML content to display inline (fallback when popups are blocked)
  if (reportHtml) {
    // We're using a separate component for report view to avoid hydration issues
    return (
      <InlineReportViewer 
        html={reportHtml} 
        onBack={() => {
          setReportHtml(null);
          navigate('/reports');
        }}
      />
    );
  }

  return (
    <div className="page-container bg-white">
      <ScreenHeader
        title={t('reports.title')}
        backLabel={t('navigation.home', { defaultValue: 'Anasayfa' })}
        showBack
      />

      <main className="main-content bg-white">
        <div className="bg-brand-25 px-4 py-5 sm:px-5 sm:py-6">
          <Tabs
            value={selectedReportType}
            onValueChange={(value) => generateReport(value as ReportType)}
            className="w-full"
          >
            <TabsList className="mb-4 grid w-full grid-cols-2 rounded-lg bg-[#EBF1FF] p-1">
              <TabsTrigger
                value="detailed"
                className="rounded-md text-[14px] font-semibold text-[#3B608F] data-[state=active]:bg-white data-[state=active]:font-bold data-[state=active]:text-brand-600 data-[state=active]:shadow-[0_1px_1px_rgba(10,13,18,0.05)]"
                title={!detailedReportAllowed ? t('reports.detailed_premium_required', { defaultValue: 'Detaylı rapor için Premium gerekli' }) : undefined}
              >
                <span className="inline-flex items-center gap-1">
                  {t('reports.detailed_report')}
                  {!detailedReportAllowed && <Lock className="h-3 w-3 opacity-70" />}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="general"
                className="rounded-md text-[14px] font-semibold text-[#3B608F] data-[state=active]:bg-white data-[state=active]:font-bold data-[state=active]:text-brand-600 data-[state=active]:shadow-[0_1px_1px_rgba(10,13,18,0.05)]"
              >
                {t('reports.general_report')}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-2">
            <h2 className="text-[18px] font-bold text-[#00315D]">
              {selectedReportType === "detailed"
                ? t('reports.detailed_report')
                : t('reports.general_report_customer', { defaultValue: 'Genel Rapor (Müşteri Raporu)' })}
            </h2>
            <p className="text-[11px] leading-[16.5px] text-[#4A5980]">
              {selectedReportType === "detailed"
                ? t('reports.detailed_report_full_description', {
                    defaultValue: 'Fotoğraflar ve tarihler ile tüm kayıtların raporu oluşturulur.',
                  })
                : t('reports.general_report_full_description', {
                    defaultValue: 'Müşteriniz için tamamlanan aşamaların zaman çizelgesi oluşturulur.',
                  })}
            </p>
          </div>
        </div>

        <div className="px-4 py-3 sm:px-5">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 pb-28">
                <FormField
                  control={form.control}
                  name="projectId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[14px] font-medium text-[#414651]">{t('reports.project_label')}</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-[44px] w-full rounded-lg border-[#D5D7DA] bg-white px-[14px] text-[16px] text-[#717680] shadow-[0_1px_2px_rgba(10,13,18,0.05)]">
                            <div className="flex items-center gap-2 text-[#717680]">
                              <Search className="h-4 w-4" />
                              <SelectValue placeholder={t('reports.search_project_placeholder', { defaultValue: 'Proje arayın' })} />
                            </div>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white border-gray-200 text-gray-900">
                          {projects.map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="includeAllRecords"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-3 rounded-xl bg-[#F2F6FA] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 space-y-[2px]">
                        <FormLabel className="text-[13px] font-semibold leading-[19.5px] text-[#101828]">
                          {t('reports.include_all_records_title', { defaultValue: 'Tüm kayıtlar' })}
                        </FormLabel>
                        <FormDescription className="text-[12px] leading-[16.5px] text-[#667085]">
                          {t('reports.include_all_records_description_short', { defaultValue: 'Varolan tüm kayıtları dahil et' })}
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          className="self-end sm:self-auto"
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            if (checked) {
                              form.setValue("startDate", undefined);
                              form.setValue("endDate", undefined);
                            }
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className={cn(
                  "grid grid-cols-1 sm:grid-cols-2 gap-4",
                  form.watch("includeAllRecords") && "opacity-50 pointer-events-none"
                )}>
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="text-[14px] font-medium text-[#414651]">{t('reports.start_date')}</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "h-[44px] w-full justify-start rounded-lg border-[#D5D7DA] pl-3 text-left text-[16px] font-normal shadow-[0_1px_2px_rgba(10,13,18,0.05)]",
                                  form.watch("includeAllRecords")
                                    ? "bg-[#F5F5F5] text-[#A4A7AE]"
                                    : "bg-white text-[#717680]",
                                  !field.value && "text-[#A4A7AE]"
                                )}
                              >
                                {field.value ? (
                                  formatInDeviceTimezone(field.value, "PPP")
                                ) : (
                                  <span>{t('reports.select_date_placeholder', { defaultValue: 'gg/aa/yyyy' })}</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 text-[#A4A7AE]" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="text-[14px] font-medium text-[#414651]">{t('reports.end_date')}</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "h-[44px] w-full justify-start rounded-lg border-[#D5D7DA] pl-3 text-left text-[16px] font-normal shadow-[0_1px_2px_rgba(10,13,18,0.05)]",
                                  form.watch("includeAllRecords")
                                    ? "bg-[#F5F5F5] text-[#A4A7AE]"
                                    : "bg-white text-[#717680]",
                                  !field.value && "text-[#A4A7AE]"
                                )}
                              >
                                {field.value ? (
                                  formatInDeviceTimezone(field.value, "PPP")
                                ) : (
                                  <span>{t('reports.select_date_placeholder', { defaultValue: 'gg/aa/yyyy' })}</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 text-[#A4A7AE]" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="includePhotos"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-3 rounded-xl bg-[#F2F6FA] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 space-y-[2px]">
                        <FormLabel className="text-[13px] font-semibold leading-[19.5px] text-[#101828]">
                          {t('reports.photos_title', { defaultValue: 'Fotoğraflar' })}
                        </FormLabel>
                        <FormDescription className="text-[12px] leading-[16.5px] text-[#667085]">
                          {t('reports.include_photos_description_short', { defaultValue: 'Proje fotoğraflarını dahil et' })}
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          className="self-end sm:self-auto"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
            </form>
          </Form>
        </div>

        <div className="sticky bottom-0 z-20 border-t border-[#CDE0F2] bg-[#F2F6FA] px-4 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] pt-2.5 sm:px-5">
          <Button
            type="button"
            className={cn(
              "h-10 w-full rounded-lg text-[14px] font-semibold shadow-[0_1px_2px_rgba(10,13,18,0.05)]",
              isLoading
                ? "bg-[#8D9FBC] text-[#F5F5F5] hover:bg-[#8D9FBC]"
                : "bg-brand-600 text-white hover:bg-brand-700"
            )}
            onClick={handleExportPDF}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {t('reports.processing')}
              </>
            ) : (
              t('reports.create_report_button', { defaultValue: 'Rapor Oluştur' })
            )}
          </Button>
          </div>
      </main>

      <BottomNavigation />

      {/* Preview Modal */}
      {currentReport && (
        <ReportModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          report={currentReport}
        />
      )}
    </div>
  );
};

// Separate component for report viewing to isolate from main app and avoid hydration errors
const InlineReportViewer: React.FC<{ html: string, onBack: () => void }> = ({ html, onBack }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [librariesLoaded, setLibrariesLoaded] = useState(false);
  const [reportLoaded, setReportLoaded] = useState(false);
  const { t } = useTranslation();
  
  // Preload PDF generation libraries
  useEffect(() => {
    const loadLibraries = async () => {
      try {
        
        
        // Check if libraries are already loaded
        if (typeof window.jspdf !== 'undefined' && typeof window.html2canvas !== 'undefined') {
          
          setLibrariesLoaded(true);
          return;
        }
        
        // Load html2canvas if not already loaded
        if (typeof window.html2canvas === 'undefined') {
          
          const html2canvasScript = document.createElement('script');
          html2canvasScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
          html2canvasScript.async = true;
          document.body.appendChild(html2canvasScript);
          
          await new Promise((resolve, reject) => {
            html2canvasScript.onload = resolve;
            html2canvasScript.onerror = () => reject(new Error('Failed to load html2canvas'));
          });
          
        }
        
        // Load jsPDF if not already loaded
        if (typeof window.jspdf === 'undefined') {
          
          const jsPdfScript = document.createElement('script');
          jsPdfScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
          jsPdfScript.async = true;
          document.body.appendChild(jsPdfScript);
          
          await new Promise((resolve, reject) => {
            jsPdfScript.onload = resolve;
            jsPdfScript.onerror = () => reject(new Error('Failed to load jsPDF'));
          });
          
        }
        
        setLibrariesLoaded(true);
      } catch (error) {
        
        toast({
          title: t('reports.pdf_libraries_load_error'),
          description: t('reports.pdf_libraries_error_description'),
          variant: "destructive"
        });
      }
    };
    
    loadLibraries();
  }, [t]);
  
  // Initialize the file export plugin bridge
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      FileExport.setupBridge()
        .then(() => {
          
        })
        .catch((error) => {
          
        });
    }
  }, []);
  
  // Function to generate PDF using jsPDF and html2canvas
  const generatePDF = async () => {
    
    setIsGeneratingPdf(true);
    toast({
      title: t('reports.preparing_report'),
      description: t('reports.pdf_creating'),
    });
    
    try {
      const iframe = document.getElementById('report-iframe') as HTMLIFrameElement;
      if (!iframe || !iframe.contentWindow || !iframe.contentDocument) {
        
        throw new Error('Could not access iframe content');
      }
      
      
      const contentDocument = iframe.contentDocument;
      const contentElement = contentDocument.body;
      
      if (!contentElement) {
        
        throw new Error('Could not access iframe content body');
      }
      
      // Get the project name from the HTML to use as filename
      const projectNameElement = contentDocument.querySelector('h1');
      const projectName = projectNameElement ? projectNameElement.textContent?.split('-')[0]?.trim() : 'Rapor';
      
      // Set white background for better PDF output
      const originalBackground = contentElement.style.backgroundColor;
      contentElement.style.backgroundColor = 'white';
      
      try {
        if (typeof html2canvas !== 'function') {
          
          throw new Error(t('reports.pdf_libraries_load_error'));
        }
        
        if (typeof jsPDF !== 'function' && (!window.jspdf || !window.jspdf.jsPDF)) {
          
          throw new Error(t('reports.pdf_libraries_load_error'));
        }
        
        const options = {
          scale: 1,
          useCORS: true,
          allowTaint: true,
          backgroundColor: 'white',
          logging: false,
          onclone: (clonedDoc: Document) => {
            try {
              const styleElement = clonedDoc.createElement('style');
              styleElement.innerHTML = `
                body { background-color: white !important; }
                table { background-color: white !important; }
                * { -webkit-print-color-adjust: exact !important; }
              `;
              clonedDoc.head.appendChild(styleElement);
              
            } catch (styleError) {
              
            }
          }
        };
        
        
        let canvas;
        try {
          canvas = await html2canvas(contentElement, options);
          
        } catch (canvasError) {
          
          if (canvasError instanceof Error) {
            
            
          }
          throw new Error(t('reports.report_page_process_error'));
        }
        
        
        
        try {
          const jsPdfConstructor = typeof jsPDF === 'function' ? jsPDF : window.jspdf.jsPDF;
          const pdf = new jsPdfConstructor({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true
          });
          
          const imgWidth = 210;
          const pageHeight = 295;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          
          
          try {
            const imgData = canvas.toDataURL('image/jpeg', 0.8);
            
            // Add image to first page
            pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
            
            // Add additional pages if content exceeds page height
            if (imgHeight > pageHeight) {
              const pageCount = Math.ceil(imgHeight / pageHeight);
              
              
              for (let i = 1; i < pageCount; i++) {
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, -(i * pageHeight), imgWidth, imgHeight);
              }
            }
            
            // Generate filename
            const filename = projectName + '-' + new Date().toLocaleDateString('tr-TR') + '.pdf';
            
            try {
              // Get PDF as base64 string or as blob
              const pdfBase64 = pdf.output('datauristring');
              
              // Use our utility function to save and share the PDF
              const result = await savePdfToDevice({
                filename,
                pdfData: pdfBase64,
                mimeType: 'application/pdf'
              });
              
              if (result.success) {
                toast({
                  title: t('reports.pdf_created_successfully'),
                  description: t('reports.pdf_ready_to_share'),
                });
              } else {
                throw new Error(result.error || 'PDF kaydedilemedi');
              }
            } catch (fileError) {
              
              
              // Last resort fallback to browser download
              pdf.save(filename);
              
              toast({
                title: t('reports.pdf_created_successfully'),
                description: t('reports.pdf_downloaded'),
              });
            }
          } catch (pdfError) {
            
            toast({
              title: t('reports.pdf_creation_error'),
              description: t('reports.pdf_creation_failed'),
              variant: "destructive"
            });
          }
        } catch (pdfInitError) {
          
          toast({
            title: t('reports.pdf_creation_error'),
            description: t('reports.pdf_preparation_error'),
            variant: "destructive"
          });
        }
      } catch (mainError) {
        
        toast({
          title: t('reports.error_occurred'),
          description: mainError instanceof Error ? mainError.message : t('reports.unexpected_error'),
          variant: "destructive"
        });
      } finally {
        // Restore original background
        contentElement.style.backgroundColor = originalBackground;
        setIsGeneratingPdf(false);
      }
    } catch (error) {
      
      toast({
        title: t('reports.error_occurred'),
        description: t('reports.report_content_access_error'),
        variant: "destructive"
      });
      setIsGeneratingPdf(false);
    }
  };
  
  return (
    <div className="page-container bg-white">
      <ScreenHeader
        title={t('reports.report_preview')}
        backLabel={t('reports.title')}
        onBack={onBack}
      />
      <div className="main-content flex flex-col bg-white">
        <div className="border-b border-[#CDE0F2] bg-brand-25 px-4 py-3 sm:px-5">
          <p className="text-[13px] font-semibold leading-[19.5px] text-[#101828]">
            {t('reports.report_preview')}
          </p>
          <p className="text-[12px] leading-[16.5px] text-[#667085]">
            {t('reports.report_inline_view_description')}
          </p>
        </div>

        {isGeneratingPdf && (
          <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
            <div className="rounded-2xl bg-white p-6 text-center shadow-[0_8px_24px_rgba(10,13,18,0.16)]">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
              <p className="text-[14px] font-medium text-[#414651]">{t('reports.pdf_creating')}</p>
            </div>
          </div>
        )}

        <div className="flex-1 bg-[#F5F7FA] px-4 py-3 sm:px-5">
          <div className="relative h-full overflow-hidden rounded-lg border border-[#D5D7DA] bg-white shadow-[0_1px_2px_rgba(10,13,18,0.05)]">
            {!reportLoaded && (
              <div className="absolute inset-x-0 top-[140px] z-10 flex justify-center">
                <div className="rounded-md bg-white/90 px-3 py-2 text-[12px] text-[#667085] shadow-sm">
                  {t('reports.preparing')}
                </div>
              </div>
            )}
          <iframe
            ref={iframeRef}
            id="report-iframe"
            srcDoc={html}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              backgroundColor: "white"
            }}
            title="Report Preview"
            onLoad={() => {
              setReportLoaded(true);
            }}
          />
          </div>
        </div>

        <div className="sticky bottom-0 z-20 border-t border-[#CDE0F2] bg-[#F2F6FA] px-4 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] pt-2.5 sm:px-5">
          <Button
            onClick={generatePDF}
            disabled={isGeneratingPdf || !librariesLoaded}
            className={cn(
              "h-10 w-full rounded-lg text-[14px] font-semibold shadow-[0_1px_2px_rgba(10,13,18,0.05)]",
              isGeneratingPdf || !librariesLoaded
                ? "bg-[#8D9FBC] text-[#F5F5F5] hover:bg-[#8D9FBC]"
                : "bg-brand-600 text-white hover:bg-brand-700"
            )}
          >
            {isGeneratingPdf ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {t('reports.processing')}
              </>
            ) : !librariesLoaded ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {t('reports.preparing')}
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                {t('reports.pdf_download')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
